<script setup lang="ts">
/**
 * La consigne de l'équipe sur une surveillance (geste 15, route 5, champ
 * `consigne` ; zone de S:6678-6682).
 *
 * Ce que l'utilisateur doit vivre : il écrit, corrige ou efface ce que
 * l'équipe doit faire quand l'alarme part (400 caractères au plus) ; la
 * consigne se lit sur la fiche et sur la page du lien, signée de son auteur.
 * Rien ne part si le texte n'a pas changé ; un texte vidé efface la
 * consigne (`null`). Le dialogue rend le corps par `valider`.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { AlarmesV2Modification } from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Le texte servi, `null` sans consigne. */
        texte: string | null
        enCours?: boolean
        refus?: string
    }>(),
    { enCours: false, refus: "" }
)

const emit = defineEmits<{
    valider: [corps: AlarmesV2Modification]
    fermer: []
}>()

/** La limite de la façade (`lireConsigne`). */
const LONGUEUR_MAX = 400

const saisie = ref(props.texte ?? "")
const rienAChanger = ref(false)

const getLongueur = computed(() => saisie.value.trim().length)

// ================================
// HANDLERS
// ================================
function onEnregistrer(): void {
    if (props.enCours) return
    const texte = saisie.value.trim().slice(0, LONGUEUR_MAX)
    const apres = texte === "" ? null : texte
    if (apres === (props.texte ?? null)) {
        rienAChanger.value = true
        return
    }
    emit("valider", { consigne: apres })
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreDialogue
        :titre="t('alarmesV2.actions.consigneDialogue.titre')"
        :bloque="enCours"
        @fermer="emit('fermer')"
    >
        <label class="flex flex-col gap-1 text-sm">
            <span>
                {{ t("alarmesV2.actions.consigneDialogue.titre") }}
                <span class="text-surface-600">{{
                    t("alarmesV2.actions.consigneDialogue.facultative")
                }}</span>
            </span>
            <textarea
                v-model="saisie"
                :maxlength="LONGUEUR_MAX"
                rows="4"
                class="rounded border border-surface-300 p-2"
                :placeholder="t('alarmesV2.actions.consigneDialogue.exemple')"
                data-test="consigne-saisie"
                @input="rienAChanger = false"
            />
        </label>
        <p class="text-xs text-surface-600">
            {{
                t("alarmesV2.actions.consigneDialogue.longueur", {
                    n: getLongueur,
                })
            }}
        </p>
        <p
            v-if="rienAChanger"
            class="text-sm text-surface-700"
            role="status"
            data-test="rien-a-changer"
        >
            {{ t("alarmesV2.actions.consigneDialogue.rienAChanger") }}
        </p>
        <p v-if="enCours" class="text-sm text-surface-700" role="status">
            {{ t("alarmesV2.actions.envoi") }}
        </p>
        <p
            v-else-if="refus"
            class="text-sm text-red-800"
            role="alert"
            data-test="refus"
        >
            {{ refus }}
        </p>

        <template #pied>
            <button
                type="button"
                class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                :aria-disabled="enCours ? 'true' : undefined"
                data-focus-initial
                data-test="annuler"
                @click="!enCours && emit('fermer')"
            >
                {{ t("alarmesV2.actions.annuler") }}
            </button>
            <button
                type="button"
                class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                :aria-disabled="enCours ? 'true' : undefined"
                data-test="enregistrer"
                @click="onEnregistrer"
            >
                {{ t("alarmesV2.actions.enregistrer") }}
            </button>
        </template>
    </CadreDialogue>
</template>
